import type Collapsible from './Collapsible.js'
import { LitElement } from 'lit'

import { customElement, property } from 'lit/decorators.js'
import { FocusableWhenDisabledController } from '../common/controllers/FocusableWhenDisabledController.js'
import { observe } from '../common/decorators/observe.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './CollapsibleTrigger.css'

/**
 * Collapsible Trigger is the interactive control that opens and closes its
 * [Collapsible](/components/collapsible/) panel. It must be placed inside a
 * [Collapsible](/components/collapsible/).
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for the trigger label and/or an icon.
 */
@customElement('nord-collapsible-trigger')
export default class CollapsibleTrigger extends LitElement {
  /**
   * Whether the associated panel is open. Set by the parent
   * [Collapsible](/components/collapsible/); reflected so it can be styled.
   */
  @property({ reflect: true, type: Boolean }) open = false

  /**
   * Whether the trigger is disabled. Set by the parent
   * [Collapsible](/components/collapsible/).
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  // Keeps the trigger focusable while disabled (aria-disabled + tabindex=0),
  // matching Base UI; activation is blocked by `activate()` below.
  private focusable = new FocusableWhenDisabledController(this, { disabled: () => this.disabled })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    // The host itself is the focusable button: light DOM can't delegate focus
    // into a shadow `<button>`, so we apply button semantics directly.
    this.setAttribute('role', 'button')
    this.setAttribute('aria-expanded', `${this.open}`)
    this.addEventListener('click', this.handleClick)
    this.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  // Render in the light DOM so the trigger label/icon stay as the element's own
  // content (no shadow boundary, like Table and Kbd).
  protected createRenderRoot() {
    return this
  }

  private get collapsible() {
    return this.closest<Collapsible>('nord-collapsible')
  }

  @observe('open')
  protected handleOpenChange() {
    this.setAttribute('aria-expanded', `${this.open}`)
  }

  private activate() {
    if (this.disabled) {
      return
    }

    this.collapsible?.toggle()
  }

  private handleClick = () => {
    this.activate()
  }

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.activate()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-collapsible-trigger': CollapsibleTrigger
  }
}
